// CoinGecko public API (free, no key required)
// Rate limit: ~10-30 calls/min, 60s cache
const BASE_URL = 'https://api.coingecko.com/api/v3';

export interface CryptoSearchResult {
  id: string;
  name: string;
  symbol: string;
  thumb: string;
  large: string;
  market_cap_rank: number | null;
}

export interface CryptoPrice {
  eur: number;
  usd: number;
  eur_24h_change: number | null;
  usd_24h_change: number | null;
  eur_market_cap: number | null;
  usd_market_cap: number | null;
}

// Top coins for the default selection screen (with real CoinGecko image URLs)
export const POPULAR_CRYPTOS: { id: string; name: string; symbol: string; thumb: string }[] = [
  {
    id: 'bitcoin',
    name: 'Bitcoin',
    symbol: 'BTC',
    thumb: 'https://coin-images.coingecko.com/coins/images/1/large/bitcoin.png',
  },
  {
    id: 'ethereum',
    name: 'Ethereum',
    symbol: 'ETH',
    thumb: 'https://coin-images.coingecko.com/coins/images/279/large/ethereum.png',
  },
  {
    id: 'tether',
    name: 'Tether',
    symbol: 'USDT',
    thumb: 'https://coin-images.coingecko.com/coins/images/325/large/Tether.png',
  },
  {
    id: 'binancecoin',
    name: 'BNB',
    symbol: 'BNB',
    thumb: 'https://coin-images.coingecko.com/coins/images/825/large/bnb-icon2_2x.png',
  },
  {
    id: 'solana',
    name: 'Solana',
    symbol: 'SOL',
    thumb: 'https://coin-images.coingecko.com/coins/images/4128/large/solana.png',
  },
  {
    id: 'ripple',
    name: 'XRP',
    symbol: 'XRP',
    thumb: 'https://coin-images.coingecko.com/coins/images/44/large/xrp-symbol-white-128.png',
  },
  {
    id: 'usd-coin',
    name: 'USDC',
    symbol: 'USDC',
    thumb: 'https://coin-images.coingecko.com/coins/images/6319/large/USDC.png',
  },
  {
    id: 'cardano',
    name: 'Cardano',
    symbol: 'ADA',
    thumb: 'https://coin-images.coingecko.com/coins/images/975/large/cardano.png',
  },
  {
    id: 'dogecoin',
    name: 'Dogecoin',
    symbol: 'DOGE',
    thumb: 'https://coin-images.coingecko.com/coins/images/5/large/dogecoin.png',
  },
  {
    id: 'polkadot',
    name: 'Polkadot',
    symbol: 'DOT',
    thumb: 'https://coin-images.coingecko.com/coins/images/12171/large/polkadot.jpg',
  },
  {
    id: 'avalanche-2',
    name: 'Avalanche',
    symbol: 'AVAX',
    thumb:
      'https://coin-images.coingecko.com/coins/images/12559/large/Avalanche_Circle_RedWhite_Trans.png',
  },
  {
    id: 'chainlink',
    name: 'Chainlink',
    symbol: 'LINK',
    thumb: 'https://coin-images.coingecko.com/coins/images/877/large/Chainlink_Logo_500.png',
  },
  {
    id: 'stellar',
    name: 'Stellar',
    symbol: 'XLM',
    thumb: 'https://coin-images.coingecko.com/coins/images/100/large/fmpFRHHQ_400x400.jpg',
  },
  {
    id: 'litecoin',
    name: 'Litecoin',
    symbol: 'LTC',
    thumb: 'https://coin-images.coingecko.com/coins/images/2/large/litecoin.png',
  },
  {
    id: 'the-open-network',
    name: 'Toncoin',
    symbol: 'TON',
    thumb:
      'https://coin-images.coingecko.com/coins/images/17980/large/photo_2024-09-10_17.09.00.jpeg',
  },
  {
    id: 'matic-network',
    name: 'Polygon',
    symbol: 'POL',
    thumb: 'https://coin-images.coingecko.com/coins/images/4713/large/polygon.png',
  },
];

// Simple in-memory cache to reduce API calls
const priceCache = new Map<string, { data: CryptoPrice; ts: number }>();
const CACHE_TTL = 60_000; // 60s

export async function searchCryptos(query: string): Promise<CryptoSearchResult[]> {
  if (!query || query.length < 1) return [];
  const url = `${BASE_URL}/search?query=${encodeURIComponent(query)}`;
  const res = await fetch(url);
  if (!res.ok) return [];
  const data = await res.json();
  return (data.coins || []).slice(0, 12).map((c: Record<string, unknown>) => ({
    id: c.id as string,
    name: c.name as string,
    symbol: c.symbol as string,
    thumb: c.thumb as string,
    large: c.large as string,
    market_cap_rank: c.market_cap_rank as number | null,
  }));
}

export async function getCryptoPrice(coinId: string): Promise<CryptoPrice | null> {
  const cached = priceCache.get(coinId);
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

  const url = `${BASE_URL}/simple/price?ids=${encodeURIComponent(coinId)}&vs_currencies=eur,usd&include_24hr_change=true&include_market_cap=true`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const data = await res.json();
  const coin = data[coinId];
  if (!coin) return null;

  const price: CryptoPrice = {
    eur: coin.eur ?? 0,
    usd: coin.usd ?? 0,
    eur_24h_change: coin.eur_24h_change ?? null,
    usd_24h_change: coin.usd_24h_change ?? null,
    eur_market_cap: coin.eur_market_cap ?? null,
    usd_market_cap: coin.usd_market_cap ?? null,
  };
  priceCache.set(coinId, { data: price, ts: Date.now() });
  return price;
}

export interface CryptoChartPoint {
  timestamp: number;
  price: number;
}

const chartCache = new Map<string, { data: CryptoChartPoint[]; ts: number }>();
const CHART_CACHE_TTL = 5 * 60_000; // 5 min

export async function getCryptoChart(
  coinId: string,
  currency: 'eur' | 'usd',
  days: number
): Promise<CryptoChartPoint[]> {
  const key = `${coinId}_${currency}_${days}`;
  const cached = chartCache.get(key);
  if (cached && Date.now() - cached.ts < CHART_CACHE_TTL) return cached.data;

  const url = `${BASE_URL}/coins/${encodeURIComponent(coinId)}/market_chart?vs_currency=${currency}&days=${days}`;
  const res = await fetch(url);
  if (!res.ok) return [];
  const data = await res.json();
  const points: CryptoChartPoint[] = (data.prices || []).map(
    ([timestamp, price]: [number, number]) => ({ timestamp, price })
  );
  chartCache.set(key, { data: points, ts: Date.now() });
  return points;
}
